import React, { useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  alpha,
} from "@mui/material";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  RestartAlt as RestartIcon,
  Notes as NotesIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Chat as ChatIcon,
} from "@mui/icons-material";
import {
  createCandidateNote,
  getCandidateNote,
} from "../../../api/services/candidate";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import TableComponent from "../../../components/TableComponent/TableComponent";
import { formatDateTime } from "../../../base/FormatTime";
import { QK } from "../../../base/qk";
import CustomInput from "../../../components/CustomForm/CustomInput";
import CustomTextArea from "../../../components/CustomForm/CustomTextArea";

function CandidatesCommunication({ clickedId }) {
  const [tabValue, setTabValue] = useState(0);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const { data: notes, isLoading: notesLoading } = useQuery(
    [QK.candidateNotes, clickedId],
    () => getCandidateNote(clickedId),
    { enabled: !!clickedId }
  );

  const handleNoteSubmit = async (event) => {
    event.preventDefault();
    const noteData = {
      title: noteTitle,
      note: noteContent,
      candidate: clickedId,
    };
    const response = await createCandidateNote(noteData);
    if (response.status === 201) {
      enqueueSnackbar("Note created successfully", { variant: "success" });
      setNoteTitle("");
      setNoteContent("");
      queryClient.invalidateQueries(["candidateNotes", clickedId]);
    } else {
      enqueueSnackbar("Failed to create note", { variant: "error" });
    }
  };

  const columns = [
    { field: "title", headerName: "Title" },
    { field: "note", headerName: "Note" },
    {
      field: "created_at",
      headerName: "Sent At",
      render: (value) => formatDateTime(value),
    },
    { field: "sender_name", headerName: "Sent By" },
  ];

  const tabConfigs = [
    { label: "Notes", icon: <NotesIcon sx={{ fontSize: 16 }} /> },
    { label: "Email", icon: <EmailIcon sx={{ fontSize: 16 }} /> },
    { label: "SMS", icon: <SmsIcon sx={{ fontSize: 16 }} /> },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* TABS HEADER */}
      <Paper
        sx={{
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          boxShadow: "none",
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        <Box sx={{ bgcolor: "#F8FAFC" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              minHeight: 56,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 3,
                backgroundColor: "#2469F0",
              },
              "& .MuiTab-root": {
                minHeight: 56,
                fontSize: "0.85rem",
                fontWeight: 500,
                textTransform: "none",
                color: "#64748B",
                px: 3,
                "&:hover": {
                  color: "#2469F0",
                  bgcolor: alpha("#2469F0", 0.04),
                },
                "&.Mui-selected": {
                  color: "#2469F0",
                  fontWeight: 600,
                },
              },
            }}
          >
            {tabConfigs.map((tab) => (
              <Tab
                key={tab.label}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
      </Paper>

      {/* NOTES TAB */}
      {tabValue === 0 && (
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NotesIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Add Note
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                Create internal notes for this candidate
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <form onSubmit={handleNoteSubmit}>
              <Stack spacing={2}>
                <CustomInput
                  label="Title *"
                  placeholder="Enter note title"
                  size="medium"
                  fullWidth
                  required
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                />

                <CustomTextArea
                  label="Notes *"
                  placeholder="Write your notes here..."
                  rows={4}
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  required
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    type="submit"
                    startIcon={<SendIcon sx={{ fontSize: 18 }} />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "6px",
                      bgcolor: "#2469F0",
                      px: 3,
                      py: 0.75,
                      "&:hover": {
                        bgcolor: "#1A5BD4",
                        boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
                      },
                    }}
                  >
                    Send Notes
                  </Button>
                </Box>
              </Stack>
            </form>

            {notes && notes.length > 0 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #E2E8F0" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "6px",
                      bgcolor: alpha("#2469F0", 0.08),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ChatIcon sx={{ fontSize: 14, color: "#2469F0" }} />
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="#475467"
                    sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                  >
                    Previous Notes ({notes.length})
                  </Typography>
                </Stack>
                <TableComponent
                  columns={columns}
                  data={notes || []}
                  isLoading={notesLoading}
                />
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* EMAIL TAB */}
      {tabValue === 1 && (
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmailIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Send Email
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                Compose and send email to candidate
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <form>
              <Stack spacing={2}>
                <CustomInput
                  label="From"
                  placeholder="Enter your email address"
                  type="email"
                  size="medium"
                  fullWidth
                />

                <CustomInput
                  label="Subject"
                  placeholder="Enter email subject"
                  size="medium"
                  fullWidth
                />

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Box sx={{ flex: "1 1 48%", minWidth: "220px" }}>
                    <CustomInput
                      label="To"
                      placeholder="Recipient's email address"
                      type="email"
                      size="medium"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 48%", minWidth: "220px" }}>
                    <CustomInput
                      label="Attach Files"
                      type="file"
                      size="medium"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <AttachFileIcon
                            sx={{ fontSize: 18, color: "#94A3B8", mr: 1 }}
                          />
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <CustomTextArea
                  label="Email Content"
                  placeholder="Write your message here..."
                  rows={5}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    type="submit"
                    startIcon={<SendIcon sx={{ fontSize: 18 }} />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "6px",
                      bgcolor: "#2469F0",
                      px: 3,
                      py: 0.75,
                      "&:hover": {
                        bgcolor: "#1A5BD4",
                        boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
                      },
                    }}
                  >
                    Send Email
                  </Button>
                </Box>
              </Stack>
            </form>
          </Box>
        </Paper>
      )}

      {/* SMS TAB */}
      {tabValue === 2 && (
        <Paper
          sx={{
            borderRadius: "12px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "#F8FAFC",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#F59E0B", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SmsIcon sx={{ color: "#F59E0B", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Send SMS
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                Send text message to candidate
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <form>
              <Stack spacing={2}>
                <CustomInput
                  label="Recipient"
                  placeholder="Enter recipient's phone number"
                  size="medium"
                  fullWidth
                />

                <Box>
                  <CustomTextArea
                    label="SMS Content"
                    placeholder="Write your SMS message here..."
                    rows={5}
                    inputProps={{ maxLength: 160 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      color: "#94A3B8",
                      mt: 0.5,
                      fontSize: "0.7rem",
                    }}
                  >
                    {noteContent.length || 0} / 160 characters
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Button
                    variant="outlined"
                    type="reset"
                    startIcon={<RestartIcon sx={{ fontSize: 18 }} />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      borderRadius: "6px",
                      borderColor: "#D0D5DD",
                      color: "#344054",
                      px: 3,
                      py: 0.75,
                      "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    startIcon={<SendIcon sx={{ fontSize: 18 }} />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "6px",
                      bgcolor: "#2469F0",
                      px: 3,
                      py: 0.75,
                      "&:hover": {
                        bgcolor: "#1A5BD4",
                        boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
                      },
                    }}
                  >
                    Send SMS
                  </Button>
                </Box>
              </Stack>
            </form>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default CandidatesCommunication;